import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Branch, ServiceArea } from "../lib/types";
import { quoteFee, quoteFeeForBand, roadDistanceKm, type FeeQuote } from "../lib/pricing";

interface Pin {
  lat: number;
  lng: number;
}

export type Fulfillment = "delivery" | "pickup";

interface LocationCtx {
  branch: Branch | null;
  pin: Pin | null;
  area: ServiceArea | null;
  mode: "pin" | "area" | null;
  fulfillment: Fulfillment;
  distanceKm: number | null;
  setBranch: (b: Branch | null) => void;
  setPin: (p: Pin | null) => void;
  setArea: (a: ServiceArea | null) => void;
  setFulfillment: (f: Fulfillment) => void;
  clear: () => void;
  quote: (subtotal: number) => FeeQuote | null;
}

const Ctx = createContext<LocationCtx | null>(null);
// v2: branch shape gained serviceAreas — ignore older persisted objects.
const STORAGE_KEY = "kb_location_v2";

interface Persisted {
  branch: Branch | null;
  pin: Pin | null;
  area: ServiceArea | null;
  fulfillment?: Fulfillment;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Persisted) : { branch: null, pin: null, area: null };
  } catch {
    return { branch: null, pin: null, area: null };
  }
}

/** Pickup has no delivery fee and no distance/minimum gating. */
function pickupQuote(): FeeQuote {
  return {
    deliverable: true,
    distanceKm: 0,
    deliveryFee: 0,
    minOrder: 0,
    belowMin: false,
    amountToMin: 0,
    reason: "ok",
    distanceBandIndex: 0,
    sizeTierIndex: 0,
  };
}

export function LocationProvider({ children }: { children: ReactNode }) {
  const initial = load();
  const [branch, setBranch] = useState<Branch | null>(initial.branch);
  const [pin, setPinState] = useState<Pin | null>(initial.pin);
  const [area, setAreaState] = useState<ServiceArea | null>(initial.area);
  const [fulfillment, setFulfillment] = useState<Fulfillment>(
    initial.fulfillment ?? "delivery",
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ branch, pin, area, fulfillment }));
  }, [branch, pin, area, fulfillment]);

  // Picking a pin and picking an area are mutually exclusive.
  const setPin = useCallback((p: Pin | null) => {
    setPinState(p);
    if (p) setAreaState(null);
  }, []);
  const setArea = useCallback((a: ServiceArea | null) => {
    setAreaState(a);
    if (a) setPinState(null);
  }, []);

  const mode: "pin" | "area" | null = pin ? "pin" : area ? "area" : null;

  const distanceKm = useMemo(() => {
    if (!branch || !pin) return null;
    return roadDistanceKm(branch, pin);
  }, [branch, pin]);

  const quote = useCallback(
    (subtotal: number): FeeQuote | null => {
      if (!branch) return null;
      if (fulfillment === "pickup") return pickupQuote();
      if (pin && distanceKm != null) return quoteFee(branch, subtotal, distanceKm);
      if (area) return quoteFeeForBand(branch, subtotal, area.band);
      return null;
    },
    [branch, pin, area, distanceKm, fulfillment],
  );

  const value = useMemo<LocationCtx>(
    () => ({
      branch,
      pin,
      area,
      mode,
      fulfillment,
      distanceKm,
      setBranch,
      setPin,
      setArea,
      setFulfillment,
      clear: () => {
        setBranch(null);
        setPinState(null);
        setAreaState(null);
      },
      quote,
    }),
    [branch, pin, area, mode, fulfillment, distanceKm, setPin, setArea, quote],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLocation(): LocationCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLocation must be used within LocationProvider");
  return ctx;
}
